/**
 * Application prefix for all localStorage keys
 */
const APP_STORAGE_PREFIX = import.meta.env.VITE_LOCAL_STORAGE_APP_PREFIX;

/**
 * Apply application prefix to storage key
 * @param key - The original key
 * @returns Prefixed storage key
 */
const getPrefixedKey = (key: string): string => {
  return `${APP_STORAGE_PREFIX}${key}`;
};

/**
 * Safely load data from localStorage
 * @param key - The localStorage key
 * @param defaultValue - Default value if key doesn't exist or there's an error
 */
export function safeLocalStorageGet<T>(key: string, defaultValue: T): T {
  try {
    const prefixedKey = getPrefixedKey(key);
    const item = localStorage.getItem(prefixedKey);
    return item ? JSON.parse(item) : defaultValue;
  } catch (error) {
    console.error(`Error reading from localStorage key: ${key}`, error);
    return defaultValue;
  }
}

/**
 * Safely save data to localStorage
 * @param key - The localStorage key
 * @param value - The value to save
 */
export const safeLocalStorageSave = (key: string, value: any): void => {
  try {
    const prefixedKey = getPrefixedKey(key);
    localStorage.setItem(prefixedKey, JSON.stringify(value));
  } catch (error) {
    console.error(`Error saving to localStorage key: ${key}`, error);
  }
};

/**
 * Safely remove an item from localStorage
 * @param key - The localStorage key to remove
 */
export const safeLocalStorageRemove = (key: string): void => {
  try {
    const prefixedKey = getPrefixedKey(key);
    localStorage.removeItem(prefixedKey);
  } catch (error) {
    console.error(`Error removing localStorage key: ${key}`, error);
  }
};
